import {
  apiRequest,
  createAntdFilter,
  useDatadog,
  VojomoLoader
} from "@vojomo-core/shared";
import BaseLayout from "../../layouts/BaseLayout";
import { useCallback, useEffect, useState } from "react";
import { Table, Checkbox, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { useParams } from "react-router-dom";

interface DataType {
  _id: string;
  id: string;
  name: string;
  email: string;
  title: string;
  include_on_weekly_marketing_emails: string;
  owner: boolean;
  is_company_admin: boolean;
}

export const Member = () => {
  const { id } = useParams();
  // const id = useLocation().pathname.split("/")[2];
  const memberId = id;

  const { trackError } = useDatadog();

  const [memberData, setMemberData] = useState<any>({
    dataSource: []
  });
  const [memberName, setMemberName] = useState(""); // Use state for member name
  const [pageTitle, setPageTitle] = useState(""); // Use state for member name

  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const url = `/get_member_data_dict?member_id=${memberId}`;
    const data = await apiRequest({
      subRoute: "admin",
      path: url,
      method: "GET"
    });

    if (
      typeof data === "object" &&
      "member_name" in data &&
      "data_dict" in data &&
      typeof data.member_name === "string"
    ) {
      const memberName: string = data.member_name;

      setMemberName(memberName);
      setPageTitle(`Member (${memberName})`);

      setMemberData({
        dataSource: data.data_dict
      });
    } else {
      trackError("invalid_data_format", { data });
    }

    console.log("MEMBER_DATA", data);

    setLoading(false);
  }, [memberId]);

  const handleCompanyAdminChange = async (
    userId: string,
    isCompanyAdmin: boolean
  ) => {
    try {
      const response = await apiRequest<{ success: boolean; message: string }>({
        subRoute: "admin",
        path: "/update_company_admin",
        method: "PATCH",
        body: {
          user_id: userId,
          company: memberName,
          is_company_admin: isCompanyAdmin
        }
      });

      if (response.success) {
        setMemberData((prev: any) => ({
          ...prev,
          dataSource: prev.dataSource.map((item: DataType) =>
            item._id === userId
              ? { ...item, is_company_admin: isCompanyAdmin }
              : item
          )
        }));
      } else {
        trackError("company_admin_update_failed", { response });
        fetchData();
      }
    } catch (error) {
      trackError("company_admin_update_error", { error });
      fetchData();
    }
  };

  const handleOwnerChange = async (userId: string, isOwner: boolean) => {
    try {
      const response = await apiRequest<{ success: boolean; message: string }>({
        subRoute: "admin",
        path: "/update_member_owner",
        method: "PATCH",
        body: {
          member_id: memberId,
          user_id: userId,
          is_owner: isOwner
        }
      });

      if (response.success) {
        // Update local state to reflect the change
        setMemberData((prev: any) => ({
          ...prev,
          dataSource: prev.dataSource.map((item: DataType) =>
            item._id === userId ? { ...item, owner: isOwner } : item
          )
        }));
      } else {
        trackError("owner_update_failed", { response });
        // Revert the checkbox by refetching data
        fetchData();
      }
    } catch (error) {
      trackError("owner_update_error", { error });
      // Revert the checkbox by refetching data
      fetchData();
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Vojomo ID",
      dataIndex: "_id",
      key: "_id",
      ...createAntdFilter<DataType>("_id", "_id"),
      sorter: (a: any, b: any) => a._id.localeCompare(b._id),
      render: (_: any, record: any) => record._id
    },
    {
      title: "HubSpot ID",
      dataIndex: "id",
      key: "id",
      ...createAntdFilter<DataType>("id", "id"),
      sorter: (a: any, b: any) => a.id.localeCompare(b.id),
      render: (_: any, record: any) => (
        <a href={record.url} target="_blank">
          {record.id}
        </a>
      )
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      ...createAntdFilter("name", "Name"),
      sorter: (a: any, b: any) => a.name.localeCompare(b.name)
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...createAntdFilter("email", "Email"),
      sorter: (a: any, b: any) => a.email.localeCompare(b.email)
    },
    {
      title: "Owner",
      dataIndex: "owner",
      key: "owner",
      render: (_: any, record: DataType) => {
        const hasVojomoId = record._id && record._id.trim() !== "";

        if (!hasVojomoId) {
          return (
            <Tooltip title="The user hasn't created a Vojomo account yet, but will be made an owner by default when they do">
              <Checkbox checked={false} disabled />
            </Tooltip>
          );
        }

        return (
          <Checkbox
            checked={record.owner}
            onChange={(e) => handleOwnerChange(record._id, e.target.checked)}
          />
        );
      }
    },
    {
      title: "Company Admin",
      dataIndex: "is_company_admin",
      key: "is_company_admin",
      render: (_: any, record: DataType) => {
        const hasVojomoId = record._id && record._id.trim() !== "";

        if (!hasVojomoId) {
          return (
            <Tooltip title="User must have a Vojomo account to be made a company admin">
              <Checkbox checked={false} disabled />
            </Tooltip>
          );
        }

        return (
          <Checkbox
            checked={record.is_company_admin}
            onChange={(e) =>
              handleCompanyAdminChange(record._id, e.target.checked)
            }
          />
        );
      }
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      ...createAntdFilter("title", "Title"),
      sorter: (a: any, b: any) => a.title.localeCompare(b.title)
    }
    // {
    //   title: "Include on Weekly Emails",
    //   dataIndex: "include_on_weekly_marketing_emails",
    //   key: "include_on_weekly_marketing_emails",
    //   ...createAntdFilter(
    //     "include_on_weekly_marketing_emails",
    //     "Include on Weekly Emails"
    //   ),
    //   sorter: (a: any, b: any) =>
    //     a.include_on_weekly_marketing_emails.localeCompare(
    //       b.include_on_weekly_marketing_emails
    //     ),
    // },
  ];

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <BaseLayout pageTitle={pageTitle}>
      <h3>{memberName}</h3>

      {loading ? (
        <VojomoLoader />
      ) : (
        <Table
          scroll={{ y: "62.5vh" }}
          columns={columns}
          dataSource={memberData.dataSource}
        />
      )}
    </BaseLayout>
  );
};
